import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getServerURL }                                         from 'configureApi';
import reportError, { ErrorTags }                               from '../errorReporting';
import { renewSession, claimSessionEnd, hasStandardSession }    from '../expiredSessionRecovery';

/**
 * CD-8740 — `withCredentials` is set on the instance, not per call.
 *
 * Authentication is carried entirely by the HttpOnly `access_token` /
 * `access_refresh` cookies (and, on the anonymous journey, the `referral`
 * cookie). This client must therefore send credentials on every request, and
 * must never attach an `Authorization` header: cms-api-gateway strips inbound
 * `Authorization` unconditionally on the oso-api catch-all, so a bearer token is
 * not merely redundant — it is discarded.
 */
export const server = axios.create({ baseURL: getServerURL(), withCredentials: true })

export class NetworkError extends Error {
  constructor(message: string) {
    super(message)
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

export class CmsError extends NetworkError {
  code: number

  constructor(message: string, code: number) {
    super(message)
    this.code = code
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

/** Set on a request replayed after a renewal, so a second 401 is not renewed again. */
type ReplayableConfig = AxiosRequestConfig & { _sessionRenewed?: boolean }

export type AuthErrorHandlerOptions = {
  /**
   * Veto renewal for a 401. The referral journey has no refresh endpoint, so
   * the caller returns `false` there and the 401 goes straight to `onError`.
   */
  canRenew?: () => boolean
}

/**
 * CD-8740 — what a cms 401 means for the caller.
 *
 * For a **standard** session a 401 is "the access cookie lapsed", not "signed
 * out": the `access_refresh` cookie lives a week. So, as `callApi` does
 * (`lib/expiredSessionRecovery`), renew once and replay the request:
 *
 * - renewed: replay it once (marked, so a second 401 is not renewed again) and
 *   the caller never notices;
 * - refused (the session really is over): hand the 401 to `onError`, which signs
 *   the user out — once, even if a `callApi` 401 shared the same refresh;
 * - could not tell (offline / 5xx): reject with the original error and sign
 *   nobody out.
 *
 * Everything else reaches `onError` as before: a 401 with no standard session
 * (anonymous, or `canRenew()` says referral) and a replay that 401s again.
 */
export function registerAuthErrorHandler(
  onError: (error: AxiosError) => void,
  { canRenew }: AuthErrorHandlerOptions = {}
) {
  server.interceptors.response.use(
    response => response,
    (error) => {
      if (error.response?.status !== 401) return Promise.reject(error)

      const config: ReplayableConfig | undefined = error.config
      if (!config || config._sessionRenewed || !hasStandardSession() || canRenew?.() === false) {
        onError(error)
        return Promise.reject(error)
      }

      return renewSession().then((outcome) => {
        if (outcome.renewed) return server.request({ ...config, _sessionRenewed: true } as ReplayableConfig)
        if (outcome.ended && claimSessionEnd(outcome)) onError(error)
        return Promise.reject(error)
      })
    }
  )
}

const parseResponse = <R>(response: AxiosResponse<R>): R => {
  if (response.data) {
    return response.data
  }

  return response as unknown as R
}

const put = <IPayload, IResponse>(endpoint: string, payload: IPayload, config?: AxiosRequestConfig): Promise<IResponse> => server.put(endpoint, payload, config)
  .then((response: AxiosResponse<IResponse>) => parseResponse(response))
  .catch((error: AxiosError) => {
    throw handleException(error)
  })

const post = <IPayload, IResponse>(endpoint: string, payload: IPayload, config?: AxiosRequestConfig): Promise<IResponse> => server.post(endpoint, payload, config)
  .then((response: AxiosResponse<IResponse>) => parseResponse(response))
  .catch((error: AxiosError) => {
    throw handleException(error)
  })

const patch = <IPayload, IResponse>(endpoint: string, payload: IPayload, config?: AxiosRequestConfig): Promise<IResponse> => server.patch(endpoint, payload, config)
  .then((response: AxiosResponse<IResponse>) => parseResponse(response))
  .catch((error: AxiosError) => {
    throw handleException(error)
  })

const get = <IResponse>(endpoint: string, config?: AxiosRequestConfig): Promise<IResponse> => server.get(endpoint, config)
  .then((response: AxiosResponse<IResponse>) => parseResponse(response))
  .catch((error: AxiosError) => {
    throw handleException(error)
  })

const del = <IResponse>(endpoint: string, config?: AxiosRequestConfig): Promise<IResponse> => server.delete(endpoint, config)
  .then((response: AxiosResponse<IResponse>) => parseResponse(response))
  .catch((error: AxiosError) => {
    throw handleException(error)
  })

export const handleException = (error: AxiosError) => {
  // Is network issue
  if (!error.response) {
    return new NetworkError(error.message || 'Network error')
  }

  if (error.response.status === 500) {
    reportError(
      error,
      'CMS API 500 Error',
      {
        context: {
          tags: `${ErrorTags.API_AXIOS}, ${error.response.status}, ${error?.config?.method ?? 'no-http-method'}`,
          url: error?.config?.url ?? 'no-url-set',
          status: error.response.status,
          data:   error.response.data
        }
      }
    );
    return new CmsError('There was an error', error.response.status)
  }

  // Standard cms error
  const message = (error.response as AxiosResponse)?.data?.message;
  const validationErrors = (error.response as AxiosResponse)?.data?.errors?.request;
  if (validationErrors) {
    return new CmsError(getValidationMessage(validationErrors), error.response.status)
  }
  if (message) {
    return new CmsError(message, error.response.status)
  }

  return new CmsError(error.message, error.response.status)
}

/**
 * Format validation errors into a string
 * @param validationErrors - The validation errors to format e.g. { field: 'error message' } or { field: ['error message 1', 'error message 2'] }
 * @returns A string with the validation errors formatted
 */
const getValidationMessage = (validationErrors: Record<string, string | string[]>) => {
  let validationMessage = ''
  for (const field in validationErrors) {
    const fieldMessage = validationErrors[field];
    if (Array.isArray(fieldMessage)) {
      validationMessage += `${field}: ${fieldMessage.join(', ')}\n`;
    } else {
      validationMessage += `${field}: ${fieldMessage}\n`;
    }
  }
  return validationMessage;
}

export default {
  get,
  put,
  post,
  patch,
  delete: del
}
